import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Eye,
  Loader2,
  Pencil,
  Send,
  Sparkles,
  Undo2,
  Upload,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import useSWR from 'swr';

import { AppShell, Breadcrumbs } from '@/components/landing/AppShell';
import { Button } from '@/components/ui/Button';
import { api } from '@/lib/api';
import {
  packDisplayTitle,
  packEstimatedMinutes,
} from '@/lib/lesson-pack-display';
import type {
  LessonPack,
  LessonPackStatus,
} from '@/lib/schema.types';
import { cn } from '@/lib/utils';

const POLL_INTERVAL_MS = 3000;

export default function LessonPackDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: pack, error, isLoading, mutate } = useSWR<LessonPack>(
    id ? ['/lesson-packs', id] : null,
    () => api.lessonPacks.get(id),
  );

  // While the pack is still generating, poll until it flips to done/error.
  useEffect(() => {
    if (!pack) return;
    if (pack.status !== 'pending' && pack.status !== 'generating') return;
    const t = setInterval(async () => {
      try {
        const s = await api.lessonPacks.status(id);
        if (s.status !== pack.status) mutate();
      } catch {
        /* ignore transient poll errors */
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [pack, id, mutate]);

  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);

  if (isLoading) return <FullScreenStatus icon={<Loader2 className="animate-spin" />} text="Loading lesson pack…" />;
  if (error)
    return (
      <FullScreenStatus
        text={`Couldn't load pack. ${error instanceof Error ? error.message : ''}`}
      />
    );
  if (!pack) return null;

  const isReady = pack.status === 'done';
  const isPublished = pack.publishState === 'published';

  const onPublish = async () => {
    setPublishing(true);
    setPublishError(null);
    try {
      const updated = isPublished
        ? await api.lessonPacks.unpublish(pack._id)
        : await api.lessonPacks.publish(pack._id);
      mutate(updated, { revalidate: false });
    } catch (err) {
      setPublishError(err instanceof Error ? err.message : 'Action failed.');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <AppShell activeNav="lesson-packs">
      <div className="mx-auto flex w-full max-w-[1100px] flex-col px-8 pt-6 pb-16">
        <Breadcrumbs
          items={[
            { label: 'Lesson Packs', href: '/lesson-packs' },
            { label: packDisplayTitle(pack) },
          ]}
        />

        <Link
          to="/lesson-packs"
          className="mt-3 inline-flex items-center gap-1 text-[12px] text-fg-muted hover:text-fg"
        >
          <ArrowLeft size={12} /> All lesson packs
        </Link>

        <div className="mt-4 flex flex-wrap items-start justify-between gap-6 rounded-2xl border border-border bg-bg-panel px-6 py-5 shadow-sm">
          {pack.thumbnailUrl && (
            <img
              src={pack.thumbnailUrl}
              alt={`${pack.title} cover`}
              className="aspect-[16/10] w-44 shrink-0 rounded-xl border border-border-subtle object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={pack.status} />
              <PublishBadge state={pack.publishState} />
              <span className="rounded-md bg-bg-subtle px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-fg-muted">
                {pack.board}
              </span>
              <span className="rounded-md bg-bg-subtle px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-fg-muted">
                {pack.grade}
              </span>
              <span className="rounded-md bg-bg-subtle px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-fg-muted">
                {pack.subject}
              </span>
            </div>
            <h1 className="mt-3 text-[22px] font-semibold leading-tight text-fg">
              {packDisplayTitle(pack)}
            </h1>
            {pack.chapterReference && (
              <p className="mt-1 text-[13px] text-fg-muted">
                {pack.chapterReference}
              </p>
            )}
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-fg-muted">
              <span>
                <span className="text-fg-subtle">Slides:</span>{' '}
                <span className="text-fg">{pack.slides?.length ?? 0}</span>
              </span>
              <span>
                <span className="text-fg-subtle">Estimated:</span>{' '}
                <span className="text-fg">{packEstimatedMinutes(pack)} min</span>
              </span>
              {pack.publishedAt && (
                <span>
                  <span className="text-fg-subtle">Published:</span>{' '}
                  <span className="text-fg">
                    {new Date(pack.publishedAt).toLocaleString()}
                  </span>
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-col items-stretch gap-2">
            <Button
              variant="secondary"
              size="md"
              disabled={!isReady || (pack.slides?.length ?? 0) === 0}
              onClick={() => navigate(`/lesson-packs/${pack._id}/edit?preview=1`)}
            >
              <Eye size={14} /> Preview
            </Button>
            <Button
              variant="secondary"
              size="md"
              disabled={!isReady}
              onClick={() => navigate(`/lesson-packs/${pack._id}/edit`)}
            >
              <Pencil size={14} /> Edit
            </Button>
            <Button
              variant={isPublished ? 'ghost' : 'primary'}
              size="md"
              disabled={!isReady || publishing}
              onClick={onPublish}
            >
              {publishing ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  {isPublished ? 'Unpublishing…' : 'Publishing…'}
                </>
              ) : isPublished ? (
                <>
                  <Undo2 size={14} /> Unpublish
                </>
              ) : (
                <>
                  <Send size={14} /> Publish
                </>
              )}
            </Button>
          </div>
        </div>

        {pack.status === 'pending' || pack.status === 'generating' ? (
          <GeneratingBanner status={pack.status} title={packDisplayTitle(pack)} />
        ) : null}

        {pack.status === 'error' && (
          <div className="mt-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
            <AlertTriangle size={14} className="mt-0.5" />
            <div>
              <div className="font-semibold">Generation failed</div>
              {pack.generationError && (
                <p className="mt-0.5 text-[12px]">{pack.generationError}</p>
              )}
            </div>
          </div>
        )}

        {publishError && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-[12.5px] text-red-700">
            {publishError}
          </div>
        )}

        {isPublished && (
          <div className="mt-4 flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-[12.5px] text-emerald-800">
            <CheckCircle2 size={14} className="mt-0.5" />
            <div>
              <div className="font-semibold">Published to classroom</div>
              <p className="mt-0.5">
                This lesson pack is now live and available to the classroom app.
                Editing any slide moves it back to draft until you publish again.
              </p>
            </div>
          </div>
        )}

        {/* Learning objectives + concepts */}
        {(pack.learningObjectives?.length ?? 0) > 0 && (
          <Section title="Learning objectives" icon={<Sparkles size={14} />}>
            <ul className="ml-4 list-disc space-y-1 text-[13px] text-fg-muted">
              {pack.learningObjectives!.map((obj, i) => (
                <li key={i}>{obj}</li>
              ))}
            </ul>
          </Section>
        )}

        {(pack.concepts?.length ?? 0) > 0 && (
          <Section title="Concepts">
            <div className="flex flex-wrap gap-2">
              {pack.concepts!.map((c) => (
                <span
                  key={c.id}
                  className="rounded-md border border-border bg-white px-2 py-1 text-[12px] text-fg"
                >
                  {c.label}
                </span>
              ))}
            </div>
          </Section>
        )}

        {/* Slide outline */}
        {isReady && pack.slides && pack.slides.length > 0 && (
          <Section title={`Slides (${pack.slides.length})`}>
            <ol className="space-y-1.5">
              {pack.slides.map((slide, i) => (
                <li
                  key={slide.id}
                  className="flex items-center gap-3 rounded-md border border-border-subtle bg-bg-panel px-3 py-2 text-[12.5px]"
                >
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-md bg-bg-subtle text-[10.5px] font-semibold text-fg-muted">
                    {i + 1}
                  </span>
                  <span className="flex-1 truncate font-medium text-fg">
                    {slide.label || slide.id}
                  </span>
                  {slide.beatMeta?.kind && (
                    <span className="rounded bg-accent-soft/60 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-accent">
                      {slide.beatMeta.kind}
                    </span>
                  )}
                  {typeof slide.beatMeta?.estimatedDurationSec === 'number' && (
                    <span className="text-[11px] text-fg-subtle">
                      ~{Math.round(slide.beatMeta.estimatedDurationSec)}s
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </Section>
        )}

        {(pack.assetManifest?.length ?? 0) > 0 && (
          <Section title={`Asset manifest (${pack.assetManifest!.length})`} icon={<Upload size={14} />}>
            <ul className="space-y-1 text-[12px] text-fg-muted">
              {pack.assetManifest!.slice(0, 8).map((a) => (
                <li
                  key={a.assetId}
                  className={cn(
                    'flex items-center gap-2 rounded-md border border-border-subtle bg-white px-2 py-1.5',
                    !a.sha256 && 'border-amber-200 bg-amber-50',
                  )}
                >
                  <span className="font-mono text-[11px] text-fg">{a.assetId}</span>
                  <span className="ml-auto truncate text-[11px] text-fg-subtle">
                    {a.mime ?? a.error ?? '—'}
                  </span>
                </li>
              ))}
              {pack.assetManifest!.length > 8 && (
                <li className="text-[11px] text-fg-subtle">
                  …and {pack.assetManifest!.length - 8} more
                </li>
              )}
            </ul>
          </Section>
        )}
      </div>
    </AppShell>
  );
}

function GeneratingBanner({
  status,
  title,
}: {
  status: LessonPackStatus;
  title: string;
}) {
  return (
    <div className="mt-4 flex items-center gap-3 rounded-md border border-accent/30 bg-accent-soft/40 px-4 py-3 text-[13px] text-fg">
      <Loader2 size={16} className="animate-spin text-accent" />
      <div>
        <div className="font-semibold">
          {status === 'pending' ? 'Queued for generation…' : 'Generating…'}
        </div>
        <p className="mt-0.5 text-[12px] text-fg-muted">
          {title
            ? `Working on "${title}". This page will refresh automatically when it's ready.`
            : 'This page will refresh automatically when it’s ready.'}
        </p>
      </div>
    </div>
  );
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-6">
      <h2 className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-fg-subtle">
        {icon}
        {title}
      </h2>
      {children}
    </section>
  );
}

function StatusBadge({ status }: { status: LessonPackStatus }) {
  if (status === 'done')
    return (
      <span className="rounded-md bg-fg/85 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-white">
        Ready
      </span>
    );
  if (status === 'error')
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-red-500 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-white">
        <AlertTriangle size={9} /> Error
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-accent px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-white">
      <Loader2 size={9} className="animate-spin" />
      {status === 'pending' ? 'Queued' : 'Generating'}
    </span>
  );
}

function PublishBadge({ state }: { state: 'draft' | 'published' }) {
  if (state === 'published')
    return (
      <span className="rounded-md bg-emerald-500 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-white">
        Published
      </span>
    );
  return (
    <span className="rounded-md border border-border bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-fg-muted">
      Draft
    </span>
  );
}

function FullScreenStatus({ text, icon }: { text: string; icon?: React.ReactNode }) {
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-bg text-sm text-fg-muted">
      <div className="flex items-center gap-2">
        {icon}
        <span>{text}</span>
      </div>
    </div>
  );
}
